import type { GraphemeLessonPart, Level } from "#level";

/** The zero-based global pronunciation-level position used by `N-P{k}` labels. */
export function findPronunciationLevelIndex(
  levels: readonly Pick<Level, "index" | "pronunciationCharacters">[],
  targetLevel: Pick<Level, "index">,
): number | undefined {
  let pronunciationIndex = 0;
  for (const level of levels) {
    if (level.index === targetLevel.index) {
      return level.pronunciationCharacters === undefined ? undefined : pronunciationIndex;
    }
    if (level.pronunciationCharacters !== undefined) {
      pronunciationIndex += 1;
    }
  }
  return undefined;
}

/**
 * A pronunciation level after character level N is `{N}-P{k}`: it follows its level the way word
 * level `{N}-W{k}` does. The P number counts every pronunciation level in curriculum order
 * (hardcoded lists in `HARDCODED_PRONUNCIATION_LEVELS`, plus any auto-derived meaning-only runs).
 */
export function formatPronunciationLevelLabel(
  level: Pick<Level, "index">,
  /** Zero-based position among the script's pronunciation levels, in curriculum order. */
  pronunciationIndex = 0,
): string {
  return `${level.index + 1}-P${pronunciationIndex + 1}`;
}

/**
 * The part a lesson introduces: primitive, glyph meaning, character with readings, or a separate
 * pronunciation lesson. Generated per-glyph metadata carries the teaching intent.
 */
export function getGraphemeLessonPart({
  grapheme,
  isPronunciationLesson,
  level,
}: {
  grapheme: string;
  isPronunciationLesson: boolean;
  level: Pick<Level, "lessonParts" | "meaningOnly">;
}): GraphemeLessonPart {
  if (isPronunciationLesson) {
    return "pronunciation";
  }
  return (
    level.lessonParts?.[grapheme] ??
    (level.meaningOnly === true ? "meaning" : "meaning-and-pronunciation")
  );
}

/**
 * The level whose lesson of `grapheme` this is: the first level teaching the character, or, for a
 * pronunciation-level lesson, the level whose pronunciation level lists it.
 */
export function findGraphemeLessonLevel<
  L extends Pick<Level, "characters" | "pronunciationCharacters">,
>(
  levels: readonly L[],
  { grapheme, part }: { grapheme: string; part?: GraphemeLessonPart },
): L | undefined {
  return part === "pronunciation"
    ? levels.find((level) => level.pronunciationCharacters?.includes(grapheme) === true)
    : levels.find((level) => level.characters.includes(grapheme));
}
